/**
 * "Records waiting on you" (13-29): the register records waiting on the person, under the queue and above the rail's
 * "In others' hands", in the rail's section style (design/mock-13: serif heading, quiet rows with a top rule).
 *
 *   - One heading per group (Decisions due, Notices due, To triage, To close or approve, Due or past due), then the
 *     rows: the ref, the record's title as the register holds it, the register and why it is waiting, and each due
 *     line (`D MMM YYYY`; past due in amber, never red, D-13-15-4). A row is a link to `/record/:ref`, at its panel.
 *   - Nothing waiting: the section is not shown at all (the all-clear stays the Action Register's moment).
 *   - A failed read: one plain line and "Try again", never a blank section. A failed refresh keeps the last view.
 *   - Keyboard: every row is a link in reading order. No motion of its own (the global reduced-motion rule holds).
 *
 * 13.2-10 (D-13.2-A-1, D-13.2-P-5):
 *   - **Complete the report** (`to-complete`, an intake stub) has its label and a quiet document glyph. While the To
 *     triage group holds one, its heading reads "To complete or triage" (D-13.2-10-3); otherwise "To triage".
 *   - **The CM's draft** on a decision: "CM's view: SIRS-P1. “their note…”" (the note shortened to one line's worth).
 *     It is a view to help the decider, never the decision.
 *   - **A provisional due** (from the draft, "If confirmed: …") reads italic with a dashed rule, so it never passes as
 *     a decided deadline; once passed it is amber with "past due", never red.
 */

import { Link } from 'react-router-dom';
import type { UseRecordsWaitingResult } from '../../hooks/useRecordsWaiting';
import type { QueueItem, WaitingGroup } from '../../lib/recordQueue';
import styles from './today.module.css';

export interface RecordsWaitingProps {
  state: Pick<UseRecordsWaitingResult, 'status' | 'groups' | 'error' | 'refreshError' | 'preparerNote' | 'reload'>;
}

/** The longest the CM's note runs on the row before it is shortened (the record page has it whole). */
const NOTE_MAX = 120;

/** D-13.2-10-3: To triage also holds "Complete the report"; the heading says so only while it does. */
function headingOf(group: WaitingGroup): string {
  if (group.key === 'triage' && group.items.some((i) => i.kinds.includes('to-complete'))) return 'To complete or triage';
  return group.heading;
}

function shortNote(note: string): string {
  const flat = note.replace(/\s+/g, ' ').trim();
  return flat.length <= NOTE_MAX ? flat : `${flat.slice(0, NOTE_MAX - 1).trimEnd()}…`;
}

/** A report to complete: a page with a pencil. Decorative; the reason says it in words. */
const CompleteIcon = () => (
  <svg className={styles.recIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" data-icon="to-complete">
    <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h4" />
    <path d="M14 3l5 5h-5z" />
    <path d="M9 12h5M9 16h2" />
    <path d="M20.4 13.6a1.4 1.4 0 0 0-2-2L14 16v2h2z" />
  </svg>
);

function Draft({ draft }: { draft: NonNullable<QueueItem['draft']> }) {
  const note = draft.note ? shortNote(draft.note) : '';
  return (
    <span className={styles.recDraft}>
      CM&rsquo;s view: {draft.value}.{note ? ` “${note}”` : ''}
    </span>
  );
}

export default function RecordsWaiting({ state }: RecordsWaitingProps) {
  const { status, groups, error, refreshError, preparerNote, reload } = state;

  if (status === 'error') {
    return (
      <section className={styles.records} aria-labelledby="records-waiting">
        <h2 id="records-waiting">Records waiting on you</h2>
        <div className={styles.recordsProblem} role="alert">
          <span>Your register records couldn&rsquo;t be read: {error}</span>
          <button type="button" className={`${styles.btn} ${styles.line} ${styles.sm}`} onClick={reload}>
            Try again
          </button>
        </div>
      </section>
    );
  }
  if (status !== 'ready' || groups.length === 0) return null;

  return (
    <section className={styles.records} aria-labelledby="records-waiting">
      <h2 id="records-waiting">Records waiting on you</h2>
      {refreshError && (
        <p className={styles.recordsNote} role="status">
          The last refresh didn&rsquo;t load ({refreshError}). Showing what was read before.{' '}
          <button type="button" className={`${styles.btn} ${styles.quiet} ${styles.sm}`} onClick={reload}>
            Try again
          </button>
        </p>
      )}
      {groups.map((group) => (
        <div key={group.key} className={styles.recGroup}>
          <h3 id={`records-${group.key}`}>
            {headingOf(group)} <span className={styles.recCount}>{group.items.length}</span>
          </h3>
          <ul aria-labelledby={`records-${group.key}`}>
            {group.items.map((item) => (
              <li key={item.ref}>
                <Link className={styles.recRow} to={item.href}>
                  <span className={styles.recHead}>
                    <span className={styles.recRef}>{item.ref}</span>
                    {item.kinds.includes('to-complete') && <CompleteIcon />}
                    <span className={styles.recTitle}>{item.label}</span>
                  </span>
                  <span className={styles.recMeta}>
                    {item.registerLabel} · {item.reasons.join(' · ')}
                  </span>
                  {item.draft && <Draft draft={item.draft} />}
                  {item.dues.map((due) => (
                    <span
                      key={due.text}
                      className={[due.past ? styles.recDuePast : styles.recDue, due.provisional ? styles.recProvisional : ''].filter(Boolean).join(' ')}
                      data-provisional={due.provisional ? 'true' : undefined}
                    >
                      {due.text}
                      {due.past && <span className={styles.recPast}> · past due</span>}
                    </span>
                  ))}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      ))}
      {preparerNote && <p className={styles.recordsNote}>{preparerNote}</p>}
    </section>
  );
}
